import React, { FC, RefObject, useMemo, useState } from "react";
import RefetchIcon from "@mui/icons-material/Cached";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { CsvIcon, ImgIcon, ReloadIcon } from "../../assets/icons";
import { downloadElementAsImage } from "../../common/utils";

interface Props {
	chartName?: string;
	isLoading?: boolean;
	onReload?: VoidFunction;
	containerRef: RefObject<HTMLDivElement | null>;
}

const ChartActions: FC<Props> = ({ chartName, isLoading, onReload, containerRef }) => {
	const { t } = useTranslation();

	const id = useMemo(() => chartName ?? uuid(), [chartName]);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const toggle = (event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) =>
		setAnchorEl(open ? null : event.currentTarget);
	const hide = () => setAnchorEl(null);

	const download = async () => {
		hide();
		await downloadElementAsImage(containerRef, `chart-${id}`, "jpg");
	};

	const reload = () => {
		hide();
		onReload?.();
	};

	if (isLoading) {
		return <RefetchIcon className="rotating-icon" />;
	}

	return (
		<>
			<IconButton
				sx={{
					flexShrink: 0,
					flexGrow: 0,
					width: "42px",
					height: "42px",
				}}
				onClick={toggle}
			>
				<MoreHorizIcon />
			</IconButton>
			<Menu anchorEl={anchorEl} open={open} onClose={hide}>
				<MenuItem onClick={download}>
					<ListItemIcon>
						<ImgIcon />
					</ListItemIcon>
					<Typography>{t("download as jpg")}</Typography>
				</MenuItem>
				<MenuItem disabled onClick={download}>
					<ListItemIcon>
						<CsvIcon />
					</ListItemIcon>
					<Typography>{t("download CSV")}</Typography>
				</MenuItem>
				{onReload && (
					<MenuItem onClick={reload}>
						<ListItemIcon>
							<ReloadIcon />
						</ListItemIcon>
						<Typography>{t("reload")}</Typography>
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

export default ChartActions;
